import { gql, useQuery, useReactiveVar } from '@apollo/client';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { Checkbox, CircularProgress, Typography, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { orderBy } from 'lodash';
import React, { type ChangeEvent, useEffect, useMemo, useState } from 'react';
import allViews from 'src/api/viewModels/allViews';
import DateRangeFilter from 'src/components/DateRangeFilter/DateRangeFilter';
import { itemSearchQuery } from 'src/dynamoQueries/search';
import styles from 'src/HomePage/DrawerAndPushedContents/LeftSideDrawerContent/SideDrawerFilter/SideDrawerFilterStyled';
import { cleanPayload } from 'src/helpers/common';
import { formatDateFilterValue, isDateField } from 'src/helpers/dateFields';
import { getExistingRangeForCategory } from 'src/helpers/filterPersistence';
import { fieldScopeToItemType, matchesFieldScope, scopeToItemType } from 'src/helpers/scope';
import { processSearchParams } from 'src/helpers/searchHelper';
import viewStateVar from 'src/store/viewState';
import type { BaseSearchParams } from 'src/utils/searchSchemas';

interface FieldNode {
  id: string;
  slug: string;
  fieldType?: string;
  dateDisplayStyle?: string;
  scope?: string[];
}

interface ViewFieldNode {
  id: string;
  sequence: number;
  fieldLabel: string;
  field: FieldNode;
}

interface ViewFieldEdge {
  node: ViewFieldNode;
}

interface FacetItem {
  key: string;
  doc_count: number;
  label?: string;
}

interface FilterChip {
  choice: string;
  category: string;
  chip: string;
}

interface FilterCategory {
  id: string;
  name: string;
  label: string;
  fieldType: string;
  dateDisplayStyle: string;
}

interface ViewModel {
  viewfieldSet?: {
    edges: ViewFieldEdge[];
  };
}

const SideDrawerFilter: React.FC = () => {
  const classes = styles({});
  const theme = useTheme();
  const { filterChips } = useReactiveVar(viewStateVar);
  const navigate = useNavigate();
  // Get typed search params from TanStack Router - this is the source of truth
  const search = useSearch({ strict: false }) as BaseSearchParams;
  const [categories, setCategories] = useState<ViewFieldEdge[]>([]);
  const [facets, setFacets] = useState<FacetItem[]>([]);
  const [currentChoices, setCurrentChoices] = useState<string[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  // Local category state — decoupled from viewStateVar.filter.category
  // so the left panel doesn't activate the top bar's dropdowns
  const [selectedCategory, setSelectedCategory] = useState<FilterCategory>({
    id: '',
    name: '',
    label: '',
    fieldType: '',
    dateDisplayStyle: '',
  });
  const [dateRangeMode, setDateRangeMode] = useState(false);

  const {
    data: categoriesData,
    loading: loading1,
    error: categoriesError,
  } = useQuery(allViews, {
    variables: {
      viewName: 'Library',
      componentName: 'FilterBarFirstDropdown',
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
  });

  useEffect(() => {
    const cleanedData = cleanPayload<ViewModel>(categoriesData);
    const viewModel = cleanedData[0];
    setCategories(viewModel?.viewfieldSet?.edges || []);
  }, [categoriesData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: addFilter is stable and uses currentChoices internally
  useEffect(() => {
    addFilter();
  }, [currentChoices]);

  const buildFacetsQuery = () => {
    const query = itemSearchQuery(false);
    query.query.opensearch.facets = true;
    query.query.opensearch.facetsMeta = true;

    const gQuery = jsonToGraphQLQuery(query, { pretty: true });
    return gql(gQuery);
  };

  const selectedFieldScope = useMemo(
    () => categories.find((item) => item.node.field.id === selectedCategory.id)?.node.field.scope,
    [categories, selectedCategory.id],
  );
  const categoryScopedItemType = fieldScopeToItemType(selectedFieldScope);
  const itemType = scopeToItemType(search.scope) ?? categoryScopedItemType;

  const { loading } = useQuery(buildFacetsQuery(), {
    fetchPolicy: 'network-only',
    skip: !selectedCategory.id,
    variables: {
      q: `${processSearchParams(search).replace('%3A', ':')}&facets=${selectedCategory.id}`,
      itemType,
    },
    onCompleted: (data) => {
      const parsed = JSON.parse(data.opensearch.facetsMeta);
      const facetsData = parsed[selectedCategory.name] as FacetItem[];
      setFacets(facetsData);
    },
  });

  const scope = search.scope || 'all';
  const scopedCategories = useMemo(
    () =>
      orderBy(categories, ['node.sequence'], ['asc']).filter((item) => matchesFieldScope(item.node.field.scope, scope)),
    [categories, scope],
  );

  useEffect(() => {
    if (!selectedCategory.id) return;

    const selectedCategoryInScope = scopedCategories.some((item) => item.node.field.id === selectedCategory.id);
    if (selectedCategoryInScope) return;

    // Category went out of scope (e.g., scope changed), collapse
    setSelectedCategory({ id: '', name: '', label: '', fieldType: '', dateDisplayStyle: '' });
    setExpandedNodes([]);
    setFacets([]);
  }, [selectedCategory.id, scopedCategories]);

  const handleClickCategory = (item: ViewFieldEdge): void => {
    if (expandedNodes.includes(item.node.id)) {
      setSelectedCategory({ id: '', name: '', label: '', fieldType: '', dateDisplayStyle: '' });
      setFacets([]);
      setExpandedNodes([]);
      setDateRangeMode(false);
    } else {
      setSelectedCategory({
        id: item.node.field.id,
        name: item.node.field.slug,
        label: item.node.fieldLabel,
        fieldType: item.node.field.fieldType ?? '',
        dateDisplayStyle: item.node.field.dateDisplayStyle ?? '',
      });
      setExpandedNodes([item.node.id]);
      setDateRangeMode(false);
    }
  };

  const showClearFilters = (): boolean => {
    return (filterChips as FilterChip[]).length !== 0 || selectedCategory.id !== '';
  };

  const resetFilters = (): void => {
    handleDeleteAllChip();
  };

  const handleDeleteAllChip = (): void => {
    setChips([]);
    setSelectedCategory({ id: '', name: '', label: '', fieldType: '', dateDisplayStyle: '' });
    setExpandedNodes([]);
    setFacets([]);
    // Update URL via TanStack Router navigate - clear all filters
    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        filters: [],
        page: 1,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });
    viewStateVar({
      ...viewStateVar(),
      showHorizontal: false,
      horizontalMediaCardDetails: { image: '', title: '' },
      akomiMediaCard: [],
      horizontalMediaCardIndex: 0,
      pagination: { ...viewStateVar().pagination, cursor: null },
    });
  };

  const setChips = (chips: FilterChip[]): void => {
    viewStateVar({
      ...viewStateVar(),
      filterChips: chips,
    });
  };

  const handleChoice = (event: ChangeEvent<HTMLInputElement>, key: string, item: ViewFieldEdge): void => {
    const { value } = event.target;
    event.stopPropagation();
    //if checkbox is unchecked add the filter
    if (existingFilter(key) === false) {
      setCurrentChoices([value]);
    }
    //if checkbox is already checked then delete the filter chip
    else {
      const typedFilterChips = filterChips as FilterChip[];
      const newChips = typedFilterChips.filter((chip) => chip.choice !== key);
      // Update URL via TanStack Router navigate
      const newFilters = newChips.map((c) => `${c.category}:${c.choice}`);
      navigate({
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          filters: newFilters,
          page: 1,
          // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
        })) as any,
      });
      viewStateVar({
        ...viewStateVar(),
        filterCategoryExclude: newChips.map((chip) => chip.category),
        filterChips: newChips,
      });
    }
  };

  const addFilter = (): void => {
    if (selectedCategory.name && currentChoices?.length !== 0) {
      const mychips = currentChoices?.map((choice) => {
        const displayValue = facets?.find((f) => f.key === choice)?.label ?? choice;
        return {
          choice: choice,
          category: selectedCategory.name,
          chip: `${selectedCategory.label}: ${displayValue}`,
        };
      });

      const typedFilterChips = filterChips as FilterChip[];
      const chips = [...typedFilterChips, ...mychips];
      // Update URL via TanStack Router navigate
      const newFilters = chips.map((c) => `${c.category}:${c.choice}`);
      navigate({
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          filters: newFilters,
          page: 1,
          // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
        })) as any,
      });
      setChips(chips);
      viewStateVar({
        ...viewStateVar(),
        pagination: { ...viewStateVar().pagination, cursor: null },
      });

      setCurrentChoices([]);
    }
  };

  function existingFilter(key: string): boolean {
    const typedFilterChips = filterChips as FilterChip[];
    for (let i = 0; i < typedFilterChips.length; i++) {
      if (typedFilterChips[i].choice === key) {
        return true;
      }
    }
    return false;
  }

  function determineColor(key: string): string {
    const typedFilterChips = filterChips as FilterChip[];
    for (let i = 0; i < typedFilterChips.length; i++) {
      if (typedFilterChips[i].choice === key) {
        return theme.palette.primary.main;
      }
    }
    return theme.palette.text.primary;
  }

  const getActiveFilterCount = (categorySlug: string): number => {
    const typedFilterChips = filterChips as FilterChip[];
    return typedFilterChips.filter((chip) => chip.category === categorySlug).length;
  };

  const handleApplyDateRange = (from: string, to: string): void => {
    const typedFilterChips = filterChips as FilterChip[];
    // Remove existing chips for this category
    const otherChips = typedFilterChips.filter((c) => c.category !== selectedCategory.name);
    const fromDisplay = formatDateFilterValue(from, selectedCategory.fieldType, selectedCategory.dateDisplayStyle);
    const toDisplay = formatDateFilterValue(to, selectedCategory.fieldType, selectedCategory.dateDisplayStyle);
    const rangeChip: FilterChip = {
      choice: `${from}..${to}`,
      category: selectedCategory.name,
      chip: `${selectedCategory.label}: ${fromDisplay} \u2013 ${toDisplay}`,
    };
    const chips = [...otherChips, rangeChip];
    const newFilters = chips.map((c) => `${c.category}:${c.choice}`);
    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        filters: newFilters,
        page: 1,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });
    setChips(chips);
    viewStateVar({
      ...viewStateVar(),
      pagination: { ...viewStateVar().pagination, cursor: null },
    });
    setDateRangeMode(false);
  };

  if (loading1) return <CircularProgress color="secondary" />;
  if (categoriesError) return <div>Error loading filters: {categoriesError.message}</div>;

  return (
    <div className={classes.drawerWrapper}>
      <div className={classes.filterTextIcon}>
        <Typography className={classes.filterText}>Filters</Typography>
      </div>
      <Divider className={classes.divider} />
      <div className={classes.treeViewWrapper}>
        {scopedCategories.length === 0 && (
          <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>No filter categories configured</Typography>
        )}
        {scopedCategories.map((item) => (
          <div key={item.node.id} className={classes.categoryContainer}>
            <button type="button" className={classes.categoryHeader} onClick={() => handleClickCategory(item)}>
              <span className={classes.treeItemWrapper}>
                <div className={classes.categoryIcon}>
                  {expandedNodes.includes(item.node.id) ? <ExpandMore /> : <ChevronRight />}
                </div>
                <div className={classes.categoryLabel}>{item.node.fieldLabel}</div>
                {(() => {
                  const count = getActiveFilterCount(item.node.field.slug);
                  return count > 0 ? <span className={classes.filterCountBadge}>{count}</span> : null;
                })()}
              </span>
            </button>
            {expandedNodes.includes(item.node.id) && (
              <div className={classes.facetsList}>
                {loading ? (
                  <CircularProgress color="secondary" size={20} />
                ) : isDateField(item.node.field.fieldType) && dateRangeMode ? (
                  <DateRangeFilter
                    facets={facets ?? []}
                    loading={loading}
                    fieldType={selectedCategory.fieldType}
                    dateDisplayStyle={selectedCategory.dateDisplayStyle}
                    existingRange={getExistingRangeForCategory(filterChips as FilterChip[], selectedCategory.name)}
                    onApplyRange={handleApplyDateRange}
                    onBack={() => setDateRangeMode(false)}
                  />
                ) : facets?.length > 0 ? (
                  <>
                    {isDateField(item.node.field.fieldType) && (
                      <Button
                        size="small"
                        onClick={() => setDateRangeMode(true)}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          fontSize: 11,
                          color: 'secondary.main',
                          p: '4px 0',
                          mb: 0.5,
                          minWidth: 0,
                          '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                        }}
                      >
                        Select range...
                      </Button>
                    )}
                    {facets.map((menuItem) => (
                      <div className={classes.choiceItem} key={menuItem.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              value={menuItem.key}
                              checked={existingFilter(menuItem.key)}
                              onChange={(event) => handleChoice(event, menuItem.key, item)}
                              className={classes.treeItemCheckbox}
                              style={{ color: determineColor(menuItem.key) }}
                            />
                          }
                          label={
                            <Typography style={{ fontSize: '13px', color: determineColor(menuItem.key) }}>
                              {menuItem.label ?? menuItem.key}
                            </Typography>
                          }
                          style={{ color: determineColor(menuItem.key) }}
                        />
                        <Typography className={classes.treeItemLabel} style={{ color: determineColor(menuItem.key) }}>
                          ({menuItem.doc_count})
                        </Typography>
                      </div>
                    ))}
                  </>
                ) : (
                  <Typography sx={{ fontSize: '12px', color: 'text.disabled', marginLeft: '12px' }}>
                    No values available
                  </Typography>
                )}
              </div>
            )}
          </div>
        ))}
        <div>
          {showClearFilters() && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {' '}
              <Button className={classes.clearFiltersButton} onClick={() => resetFilters()}>
                CLEAR FILTERS
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SideDrawerFilter;
